// Permission-denied surfacing (#12888).
//
// Owner rule (2026-09-15): a denied admin action must ALWAYS tell the admin
// why. The server's canonical denial (AuthorizationDenial) is a 403 whose JSON
// body carries `code: 'permission_denied'` and the reason in `error`. The
// React API layer (app/frontend/react/lib/api.ts) dispatches
// `goat:permission-denied` for every such 403, whatever the calling screen
// does with the thrown ApiError. 180 mutations, 35 error handlers: this
// listener is the backstop for the other 145.
//
// One modal at a time, and the same reason is not re-shown within
// PERMISSION_REPEAT_WINDOW_MS (a TanStack query retries a failed GET, so a
// denied read would otherwise pop four identical dialogs).

import { showAlertModal } from './custom_confirm'

export const PERMISSION_DENIED_CODE = 'permission_denied'
export const PERMISSION_DENIED_EVENT = 'goat:permission-denied'
export const PERMISSION_DENIED_TITLE = 'Permission needed'
export const PERMISSION_REPEAT_WINDOW_MS = 5000

let modalOpen = false
let lastMessage = null
let lastShownAt = 0

// The reason from a permission_denied body, or null for anything else (a
// differently-coded 403 such as crm_module_locked, a bare "Forbidden", HTML).
export function permissionDeniedMessage(body) {
  if (typeof body !== 'string' || body.length === 0) return null
  try {
    const parsed = JSON.parse(body)
    if (parsed && parsed.code === PERMISSION_DENIED_CODE && typeof parsed.error === 'string' && parsed.error) {
      return parsed.error
    }
  } catch {
    // Not JSON — nothing to surface.
  }
  return null
}

// Returns true when a modal was opened.
export function showPermissionDeniedModal(message) {
  if (!message) return false
  if (modalOpen) return false
  const now = Date.now()
  if (message === lastMessage && now - lastShownAt < PERMISSION_REPEAT_WINDOW_MS) return false

  modalOpen = true
  lastMessage = message
  lastShownAt = now
  Promise.resolve(showAlertModal(message, { title: PERMISSION_DENIED_TITLE, variant: 'warning' }))
    .finally(() => { modalOpen = false })
  return true
}

function handlePermissionDeniedEvent(event) {
  showPermissionDeniedModal(event.detail?.message)
}

let initialized = false

export function initPermissionDeniedListener() {
  if (initialized) return
  initialized = true
  document.addEventListener(PERMISSION_DENIED_EVENT, handlePermissionDeniedEvent)
  // Stimulus controllers and inline view scripts can raise it without importing.
  window.showPermissionDeniedModal = showPermissionDeniedModal
}
